import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Card, CardContent, LinearProgress, Typography } from '@mui/material';

import { colors } from 'utils/constants';
import { statsColor } from 'utils/helpers';

const LinearProgressLabel = styled.span`
  position: absolute;
  bottom: 8px;
  left: 5px;
  color: ${props => props.$statscolor || '#426ABB'};
  font-size: 12px;
`;

const StyledCard = styled(Card)`
  position: relative;
  min-width: 180px;
  max-width: 270px;
  height: 170px;
  padding: 10px;
`;

const StyledCardContent = styled(CardContent)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 0;
  text-align: center;

  &:last-child {
    padding-bottom: 0;
  }
`;

const StyledCount = styled(Typography)`
  font-size: 64px;
  line-height: 77px;
  letter-spacing: 0;
`;

const StyledLinearProgress = styled(LinearProgress)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background-color: ${colors.wildSand};

  .MuiLinearProgress-bar {
    background-color: ${props => props.$statscolor || colors.primary};
  }
`;

/**
 * Fixed-size MUI `Card` displaying a single large statistic count above a
 * caption, with an optional bottom progress bar showing the count as a
 * percentage of `maxCount`. Use for individual metric cards on dashboard and
 * overview pages.
 */
const StatisticCard = ({ count, hasPercentageBar, isPrimaryColor, isOutlined, maxCount, text }) => {
  const percentageValue = (count / maxCount) * 100 || 0;

  return (
    <StyledCard variant={isOutlined ? 'outlined' : 'elevation'}>
      <StyledCardContent>
        <StyledCount color={isPrimaryColor ? 'primary' : 'secondary'}>{count}</StyledCount>
        <Typography variant="subtitle1">{text}</Typography>
        {hasPercentageBar && (
          <>
            <LinearProgressLabel $statscolor={statsColor(text)} variant="subtitle2">
              {percentageValue.toFixed(1)}%
            </LinearProgressLabel>
            <StyledLinearProgress
              $statscolor={statsColor(text)}
              value={percentageValue}
              variant="determinate"
            />
          </>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};

StatisticCard.defaultProps = {
  count: 0,
  hasPercentageBar: false,
  isOutlined: false,
  isPrimaryColor: true,
  maxCount: 0,
  text: '',
};

StatisticCard.propTypes = {
  count: PropTypes.number,
  /** Shows a bottom progress bar and label for `count / maxCount`, colored by `statsColor(text)`. */
  hasPercentageBar: PropTypes.bool,
  isOutlined: PropTypes.bool,
  isPrimaryColor: PropTypes.bool,
  /** Denominator for the percentage bar; the bar shows 0% when this is 0. */
  maxCount: PropTypes.number,
  text: PropTypes.string,
};

export default StatisticCard;
